import React, { lazy, Suspense, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Switch, Route, useLocation, useHistory, Redirect } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import { ClimbingLoader } from './global/Spinners';
import MuiTheme from './theme';
import config from './config/config';
import MainTheme from './layout-components/MainTheme';
import ReactGA from 'react-ga';
import { extractItemsByUserPlan, getUserByToken } from './global/GlobalActions';
import wrapWithErrorBoundary from 'global/ErrorBoundary';
import Cache from './Cache';
import { useSelector } from 'react-redux';
import { getItemFromLocalStorage, removeLocalStorage, setItemInLocalStorage } from 'utils/helpers';
import { USER_PLANS } from 'config/appConfig';

const LandingPage = lazy(() => import('./components/LandingPage/LandingPage'));
const UserAuthenticator = lazy(() => import('components/signIn/UserAuthenticator'));
const AccessDenied = lazy(() => import('components/accessDenied'));
const HomePage = lazy(() => import('components/homePage'));
const WatchList = lazy(() => import('components/watchlist'));
// const SocialScreener = lazy(() => import('components/socialScreener'));
const Topic = lazy(() => import('components/topic'));
const Guidelines = lazy(() => import('components/guidelines'));
const Filings = lazy(() => import('components/Filings'));
const Comparision = lazy(() => import('components/comparision'));
const Sentiment = lazy(() => import('components/sentiment'));
const SocialSentiment = lazy(() => import('components/socialSentiment'));
const News = lazy(() => import('components/news'));
const Settings = lazy(() => import('components/userSettings'));

const pageVariants = {
  initial: { opacity: 0 /* scale: 0.99 */ },
  in: { opacity: 1, scale: 1 },
  out: { opacity: 0, scale: 1.01 },
};

const pageTransition = { type: 'tween', ease: 'anticipate', duration: 0.4 };
const { udt, udtThemeX, noLicenseNeeded } = USER_PLANS;

const authRoutes = [
  { path: '/PagesRegister', component: UserAuthenticator },
  { path: '/ImpersonateLogin', component: UserAuthenticator },
  { path: '/accessDenied', component: AccessDenied },
];

const routes = [
  { path: '/home', component: HomePage, license: udt },
  { path: '/watchlist', component: WatchList, license: udt },
  // { path: '/social-screener', component: SocialScreener, license: udt },
  { path: '/filings', component: Filings, license: udt },
  { path: '/comparision', component: Comparision, license: udt },
  { path: '/sentiment', component: Sentiment, license: udt },
  { path: '/socialSentiment', component: SocialSentiment, license: udt },
  { path: '/news', component: News, license: udt },
  { path: '/topic', component: Topic, license: udtThemeX },
  { path: '/guideline', component: Guidelines, license: noLicenseNeeded },
  { path: '/settings', component: Settings, license: noLicenseNeeded },
];

const Routes = () => {
  const history = useHistory();
  const location = useLocation();
  const locationRef = useRef(location);
  const [showLoading, setShowLoading] = useState(true);
  const userPlans = useSelector(state => state.AccessController.userPlans);

  const allowedRoutes = useMemo(() => extractItemsByUserPlan(userPlans, routes), [userPlans]);

  useEffect(() => {
    (async () => {
      const path = locationRef.current.pathname;
      const userAuthToken = getItemFromLocalStorage('auth_token', null, false);
      if (userAuthToken) {
        const isValidUser = await getUserByToken();
        if (isValidUser) {
          if (['/PagesRegister', '/ImpersonateLogin', '/accessDenied'].includes(path)) {
            history.push('/home');
          }
        } else {
          localStorage.removeItem('auth_token');
          history.push(isValidUser === null ? '/accessDenied' : '/PagesRegister');
        }
      } else if (!['/PagesRegister', '/', '/ImpersonateLogin', '/accessDenied'].includes(path)) {
        removeLocalStorage();
        setItemInLocalStorage('redirect_url', window.location);
        history.push('/PagesRegister');
      }
      setShowLoading(false);
    })();
  }, [history]);

  useEffect(() => {
    config.googleAnalyticsKey && ReactGA.pageview(location.pathname);
  }, [location]);

  const getRouteComponents = useCallback(
    routes => (
      <Switch location={location} key={location.pathname}>
        <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
          {routes.map(route => (
            <Route key={route.path} path={route.path}>
              {wrapWithErrorBoundary(<route.component />, 'primary')}
            </Route>
          ))}
        </motion.div>
      </Switch>
    ),
    [location]
  );

  return showLoading ? (
    <ClimbingLoader />
  ) : (
    <ThemeProvider theme={MuiTheme}>
      <AnimatePresence>
        <Suspense fallback={<ClimbingLoader />}>
          <Switch>
            <Route path={authRoutes.map(route => route.path)} exact>
              {getRouteComponents(authRoutes)}
            </Route>
            <Route path={allowedRoutes.map(route => route.path)}>
              <Cache />
              <MainTheme>{getRouteComponents(allowedRoutes)}</MainTheme>
            </Route>
            <Route path="/" component={LandingPage} exact />
            <Redirect to="/" />
          </Switch>
        </Suspense>
      </AnimatePresence>
    </ThemeProvider>
  );
};

export default Routes;
